import {
  CommonButton,
  CustomInputLabel,
  CustomMenuItem,
  CustomSelect,
} from '../../common/StyledComponents/StyledFields';
import styles from './managementTeamcategory.module.scss';
import { Card, FormControl, Grid, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { object, string, ValidationError } from 'yup';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import ToastError from '../../common/toast/toastError';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import ErrorPopup from '../../common/ErrorPopup';
import constants from '../../../config/constants.json';
import LOCALE from '../../../config/en-us.json';
import SuccessPopup from '../../devotee/donation/successPopup';
import apiPath from '../../../config/apiPaths.json';

interface types {
  id: any;
  name: string;
}

interface SelectItem {
  id: number;
  bod: number;
  ec: number;
  sc: number;
}

type ErrorMessageTypes = {
  boardOfDirectors: string;
  executiveCommittees: string;
  steeringCommittees: string;
};

const ManagementTeamCategory = () => {
  const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
    boardOfDirectors: '',
    executiveCommittees: '',
    steeringCommittees: '',
  });
  const [formData, setFormData] = useState<{
    boardOfDirectors: any;
    executiveCommittees: any;
    steeringCommittees: any;
  }>({
    boardOfDirectors: '',
    executiveCommittees: '',
    steeringCommittees: '',
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [categoriesList, setCategoriesList] = useState<types[]>([]);
  const [selectItems, setSelectItems] = useState<SelectItem[]>([]);

  const validationSchema = object().shape({
    boardOfDirectors: string().required('Pooja Title is required'),
    executiveCommittees: string().required('Description is required'),
    steeringCommittees: string().required('Hour is required'),
  });

  useEffect(() => {
    apiRequest({
      method: 'GET',
      endpoint: apiPath?.getCategories,
      includeToken: true,
      params: {
        size: 500,
      },
    })
      .then((res) => {
        setCategoriesList(res.data.content);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          },
        );
      });
  }, []);
  useEffect(() => {
    apiRequest({
      method: 'GET',
      endpoint: apiPath.getaddedCategories,
      includeToken: true,
    })
      .then((res) => {
        setSelectItems(res.data.content);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          },
        );
      });
  }, []);

  useEffect(() => {
    if (selectItems.length > 0 && categoriesList.length > 0) {
      const selectedItem = selectItems[0];
      setFormData({
        boardOfDirectors: selectedItem?.bod,
        executiveCommittees: selectedItem?.ec,
        steeringCommittees: selectedItem?.sc,
      });
    }
  }, [selectItems, categoriesList]);

  const handleSave = async (): Promise<void> => {
    const errors: ErrorMessageTypes = {
      boardOfDirectors: '',
      executiveCommittees: '',
      steeringCommittees: '',
    };

    try {
      // Validate the form data against the validation schema
      await validationSchema.validate(formData, { abortEarly: false });
      // If validation passes, you can submit the form
      setErrorMessage(errors);
      // Call your API or perform other actions here
      saveApiCall();
    } catch (error: any) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e: any) => {
          if (e.path) {
            errors[e.path as keyof ErrorMessageTypes] = e.message;
          }
        });
      }
      setErrorMessage(errors);
    }
  };
  const saveApiCall = () => {
    const selectedId: any = selectItems?.find((item: any) => item?.id)
    const reqBody = {
      id: selectedId?.id,
      bod: formData?.boardOfDirectors,
      ec: formData?.executiveCommittees,
      sc: formData?.steeringCommittees,
    };
    apiRequest({
      method: 'POST',
      endpoint: apiPath.managementCategories,
      includeToken: true,
      body: JSON.stringify(reqBody),
    })
      .then((res) => {
        const successMessage = LOCALE.MANAGEMENT_TEAM_CATEGORIES_ADDED;
        toast.custom(
          <SuccessPopup
            headerMessage="Management Team Categories"
            showMessage={successMessage}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
        );
      })
      .catch((err) => {
        toast.custom(<ToastError message={catchErrorMessageHandler(err)} />, {
          duration: 10000,
        });
        console.log(err);
      });
  };
  const handleFieldChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };
  const boardOfDirectordsDropdown = (): React.ReactElement => {
    return (
      <FormControl sx={{ width: '100%' }} className={styles.dropDowns}>
        <CustomSelect
          IconComponent={KeyboardArrowDownIcon}
          id="grouped-select"
          placeholder="Select Option"
          value={formData.boardOfDirectors}
          onChange={(e) => {
            setErrorMessage({ ...errorMessage, boardOfDirectors: '' });
            handleFieldChange('boardOfDirectors', e.target.value);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: '1px solid #F05802',
                '& ul': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: 'auto',
                  minHeight: 'auto',
                  maxHeight: '300px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#F3C539',
                    borderRadius: '6px',
                  },
                },
              },
            },
          }}
          className={errorMessage.boardOfDirectors ? styles.errorTextField : styles.textField}
        >
          {categoriesList.map((item, index: number) => (
            <CustomMenuItem key={index} value={item.id}>
              {item.name}
            </CustomMenuItem>
          ))}
        </CustomSelect>
        {errorMessage.boardOfDirectors && (
          <Typography sx={{ color: 'red', fontSize: '12px' }}>
            {errorMessage.boardOfDirectors}
          </Typography>
        )}
      </FormControl>
    );
  };

  const executiveCommitteeDropdown = (): React.ReactElement => {
    return (
      <FormControl sx={{ width: '100%' }} className={styles.dropDowns}>
        <CustomSelect
          IconComponent={KeyboardArrowDownIcon}
          id="grouped-select"
          placeholder="Select Option"
          value={formData.executiveCommittees}
          onChange={(e) => {
            setErrorMessage({ ...errorMessage, executiveCommittees: '' });
            handleFieldChange('executiveCommittees', e.target.value);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: '1px solid #F05802',
                '& ul': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: 'auto',
                  minHeight: 'auto',
                  maxHeight: '300px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#F3C539',
                    borderRadius: '6px',
                  },
                },
              },
            },
          }}
          className={
            errorMessage.executiveCommittees ? styles.errorTextField : styles.textField
          }
        >
          {categoriesList.map((item, index: number) => (
            <CustomMenuItem key={index} value={item.id}>
              {item.name}
            </CustomMenuItem>
          ))}
        </CustomSelect>
        {errorMessage.executiveCommittees && (
          <Typography sx={{ color: 'red', fontSize: '12px' }}>
            {errorMessage.executiveCommittees}
          </Typography>
        )}
      </FormControl>
    );
  };

  const steeringCommitteeDropdown = (): React.ReactElement => {
    return (
      <FormControl sx={{ width: '100%' }} className={styles.dropDowns}>
        <CustomSelect
          IconComponent={KeyboardArrowDownIcon}
          id="grouped-select"
          placeholder="Select Option"
          value={formData.steeringCommittees}
          onChange={(e) => {
            setErrorMessage({ ...errorMessage, steeringCommittees: '' });
            handleFieldChange('steeringCommittees', e.target.value);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: '1px solid #F05802',
                '& ul': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: 'auto',
                  minHeight: 'auto',
                  maxHeight: '300px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#F3C539',
                    borderRadius: '6px',
                  },
                },
              },
            },
          }}
          className={
            errorMessage.steeringCommittees ? styles.errorTextField : styles.textField
          }
        >
          {categoriesList.map((item, index: number) => (
            <CustomMenuItem key={index} value={item.id}>
              {item.name}
            </CustomMenuItem>
          ))}
        </CustomSelect>
        {errorMessage.steeringCommittees && (
          <Typography sx={{ color: 'red', fontSize: '12px' }}>
            {errorMessage.steeringCommittees}
          </Typography>
        )}
      </FormControl>
    );
  };

  const ManagementTeamCategoryFields = () => {
    return (
      <div>
        <Typography className={styles.header}>Management Team Category</Typography>
        <Card className={styles.cardConatiner}>
          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', flexDirection: 'column', padding: '24px' }}
          >
            <Grid item xs={12} sm={3}>
              <CustomInputLabel>Board Of Directors</CustomInputLabel>
              {boardOfDirectordsDropdown()}
            </Grid>
            <Grid item xs={12} sm={3}>
              <CustomInputLabel>Executive Committee</CustomInputLabel>
              {executiveCommitteeDropdown()}
            </Grid>
            <Grid item xs={12} sm={3}>
              <CustomInputLabel>Steering Committee</CustomInputLabel>
              {steeringCommitteeDropdown()}
            </Grid>
          </Grid>
        </Card>
        <CommonButton className={styles.addnewButton} onClick={handleSave}>
          Submit
        </CommonButton>
      </div>
    );
  };
  return (
    <div className={styles.mainContainer}>
      <Toaster />
      {ManagementTeamCategoryFields()}
    </div>
  );
};

export default ManagementTeamCategory;
